import { cn } from "@/lib/utils";
import {
  QrCode,
  ChevronLeft,
  ChevronRight,
  Users,
  UserCheck,
  History,
  FileBarChart,
  Settings as SettingsIcon,
} from "lucide-react";
import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useUIStore } from "@/store/ui";

interface SidebarProps {
  className?: string;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

const getNavigationItems = (
  subscriptionType?: "full_platform" | "visitor_management_only",
): NavigationItem[] => {
  if (subscriptionType === "visitor_management_only") {
    return [
      { name: "Visitors Today", href: "/visitors", icon: UserCheck },
      { name: "History", href: "/visitors/history", icon: History },
      { name: "Reports", href: "/visitors/reports", icon: FileBarChart },
      { name: "QR Codes", href: "/qr", icon: QrCode },
    ];
  }

  return [
    { name: "Users", href: "/users", icon: Users },
    { name: "Static QR", href: "/qr", icon: QrCode },
  ];
};

export function Sidebar({ className }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const user = useAuthStore((state) => state.user);
  const baseNavigation = getNavigationItems(user?.subscriptionType);
  const navigation: NavigationItem[] = user?.isInternalAdmin
    ? [
        ...baseNavigation,
        {
          name: "Platform Config",
          href: "/platform-config",
          icon: SettingsIcon,
        },
      ]
    : baseNavigation;
  const { isDesktop, isMobile } = useBreakpoint();
  const { isMobileMenuOpen, closeMobileMenu } = useUIStore();
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    if (isMobile) {
      closeMobileMenu();
    }
  }, [location.pathname, isMobile, closeMobileMenu]);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isMobileMenuOpen, closeMobileMenu]);

  return (
    <aside
      className={cn(
        "bg-card border-r border-border text-card-foreground transition-all duration-300 backdrop-blur-sm bg-opacity-90",
        isDesktop
          ? isCollapsed
            ? "w-16"
            : "w-64"
          : cn(
              "fixed inset-y-0 left-0 z-50 w-64 transform transition-transform",
              isMobileMenuOpen ? "translate-x-0" : "-translate-x-full",
            ),
        className,
      )}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between p-4">
          {!isCollapsed && (
            <h2 className="text-lg font-semibold">Navigation</h2>
          )}
          {isDesktop && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="rounded p-1 hover:bg-accent min-h-[44px] min-w-[44px]"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        <nav
          className="flex-1 space-y-2 px-2 pb-4"
          aria-label="Primary navigation"
        >
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === "/visitors"}
              className={({ isActive }: { isActive: boolean }) =>
                cn(
                  "group flex items-center rounded-md px-3 py-3 text-lg font-medium transition-colors min-h-[52px] touch-manipulation",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )
              }
              aria-label={item.name}
            >
              <item.icon
                className={cn(
                  "mr-4 h-6 w-6 flex-shrink-0",
                  isCollapsed && "mr-0",
                )}
                aria-hidden="true"
              />
              {!isCollapsed && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>
      </div>
    </aside>
  );
}
